import { useState } from "react";
import { 
  ChevronDown, 
  ChevronRight, 
  Calculator, 
  Thermometer, 
  Target, 
  Gauge,
  Database,
  FileText,
  Calendar,
  Ship,
  Fuel,
  BarChart,
  Filter,
  Clock,
  Hash,
  AlertCircle
} from "lucide-react";

const BoilerOverview = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      collection: "VesselBoilerEngineData",
      fields: [
        "vesselId", "voyageId", "date", "cargoHeating", "cargoDischarge", 
        "generalService", "boilerConsMTHFO", "boilerConsMTLFO", "boilerConsMTMGO"
      ],
      purpose: "Boiler operational data and fuel consumption"
    },
  ];

  const coreCalculations = [
    {
      id: "total-consumption-calculation",
      title: "Total Consumption Calculation",
      description: "Sums boiler consumption across all records in date range",
      formula: "Total = Σ(cargoHeating + cargoDischarge + generalService)",
      subFormulas: [
        "cargoHeating: Sum across all records",
        "cargoDischarge: Sum across all records",
        "generalService: Sum across all records",
        "totalFuelConsumption: Sum of all fuel types",
        "Precision: .toFixed(1) for display"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "fuel-consumption-aggregation",
      title: "Fuel Consumption Aggregation",
      description: "Aggregates fuel consumption by type across all boiler records",
      formula: "Total Fuel = Σ(HFO + LFO + MGO)",
      subFormulas: [
        "HFO: boilerConsMTHFO from each record",
        "LFO: boilerConsMTLFO from each record",
        "MGO: boilerConsMTMGO from each record",
        "Sum across all records in date range",
        "Metric tons (MT) units"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    }
  ];

  const boilerMetrics = [
    {
      metric: "Cargo Heating",
      field: "cargoHeating",
      unit: "MT",
      description: "Steam used for heating cargo tanks and maintaining temperature"
    },
    {
      metric: "Cargo Discharge",
      field: "cargoDischarge",
      unit: "MT",
      description: "Steam consumed during cargo unloading operations"
    },
    {
      metric: "General Service",
      field: "generalService",
      unit: "MT",
      description: "Steam for general vessel services and utilities"
    },
    {
      metric: "Fuel Consumption",
      field: "totalFuelConsumption",
      unit: "MT",
      description: "Total fuel used (HFO + LFO + MGO) for boiler operations"
    }
  ];

  const fuelTypes = [
    { type: "HFO", field: "boilerConsMTHFO", unit: "MT", description: "Heavy Fuel Oil consumption" },
    { type: "LFO", field: "boilerConsMTLFO", unit: "MT", description: "Light Fuel Oil consumption" },
    { type: "MGO", field: "boilerConsMTMGO", unit: "MT", description: "Marine Gas Oil consumption" }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#faeebe] rounded-lg">
              <Thermometer className="w-6 h-6 text-[#b7950b]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Boiler Performance Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Steam consumption analysis, fuel tracking, and efficiency metrics with comprehensive data processing
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200 space-y-8">
            {/* Data Sources */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Data Sources & Collections</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <h4 className="font-semibold text-gray-800">{source.collection}</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{source.purpose}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="font-medium mb-1">Key Fields:</div>
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">• {field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations & Logic</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Boiler Metrics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Boiler Performance Metrics</h3>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {boilerMetrics.map((metric, index) => (
                    <div key={index} className="bg-white p-4 rounded border">
                      <div className="font-semibold text-gray-800 mb-2">{metric.metric}</div>
                      <div className="text-xs text-gray-600 mb-1">Field: <span className="font-mono">{metric.field}</span></div>
                      <div className="text-xs text-gray-600 mb-2">Unit: <span className="font-mono">{metric.unit}</span></div>
                      <p className="text-sm text-gray-700">{metric.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoilerOverview;